import { EventEmitter } from 'events';
import { createLog } from '@iv/shared/src/log';
import VObject from './v';
import { ChannelNameSpace } from '../MessageChannel';

export const log = createLog('Comps');

class UtilsVObject extends VObject {
  protected onValueUpdate(key: string, value: number): void {
    Utils.broadcast(`valueChange${key}`, value);
  }

  protected onConditionUpdate(condition: string, result: boolean): void {
    Utils.broadcast(`conditionChange${condition}`, result);
  }
}

const vObject = new UtilsVObject();
export const compShow: { [key: string]: boolean } = {};

const ctx = self as any;
export const isInWorker = typeof ctx.WorkerGlobalScope !== 'undefined' && ctx instanceof ctx.WorkerGlobalScope;
export const isInServer = typeof ctx.navigator !== 'undefined' && ctx.navigator.userAgent === 'node';

/**
 * @description 命名空间utils
 * @expample
 * ```javascript
 * // 在组件内的使用方式
 *
 * // broadcast为方法
 * // eventName和data为broadcast方法的参数
 * this.bridge.invoke('utils', 'broadcast', eventName, data);
 *
 * // preloadImg为方法
 * // imageUrl为preloadImg方法的参数
 * this.bridge.invoke('utils', 'preloadImg', imageUrl);
 * ```
 */
export class Utils {
  /**
   * @internal
   */
  public static messageMap: { [key: string]: EventEmitter } = {};

  /**
   * @internal
   */
  public static contWidth = 0;

  /**
   * @internal
   */
  public static contHeight = 0;

  /**
   * @internal
   */
  public static vObject: VObject = vObject;

  /**
   * @internal
   */
  public static broadcast(event: string, ...data: any[]) {
    Object.keys(Utils.messageMap).forEach((uid) => {
      const message = Utils.messageMap[uid];
      if (message) {
        message.emit(event, ...data);
      }
    });
  }

  private uid: string;
  private compsBridge: any;
  private destroyed: boolean;
  private message: EventEmitter;

  /**
   * @internal
   */
  public constructor(uid: string, compsBridge: any) {
    this.uid = uid;
    this.compsBridge = compsBridge;
    this.message = new EventEmitter();
    Utils.messageMap[uid] = this.message;
    this.message.setMaxListeners(0); // 关闭event最大listener限制
  }

  /**
   * @internal
   */
  public showInteract(forceShow?: boolean) {
    if (this.destroyed || compShow[this.uid]) return;
    if (Object.keys(compShow).every(uid => !compShow[uid])) {
      // show 互动层
      this.compsBridge.forceInvoke(ChannelNameSpace.engine, 'showInteract', forceShow);
    }
    compShow[this.uid] = true;
  }

  /**
   * @internal
   */
  public hideInteract() {
    if (this.destroyed || !compShow[this.uid]) return;
    compShow[this.uid] = false;
    if (Object.keys(compShow).every(uid => !compShow[uid])) {
      // hide 互动层
      this.compsBridge.forceInvoke(ChannelNameSpace.engine, 'hideInteract');
    }
  }

  /**
   * @description 更新v数值
   * @internal
   */
  public updateValues(data: {
    expression?: string;
    numbers?: Array<{ key: string; value: number; description: string }>;
  }) {
    Utils.vObject.updateValues(data);
  }

  /**
   * 获取某个数值的值
   * @param props 数值变量名
   * @example
   * ```javascript
   * // 假设数值v123=3
   * const value = await this.bridge.getValue('v123'); // value == 3
   * ```
   */
  public async getValue(props: string) {
    const value = await Utils.vObject.getValue(props);
    return value;
  }

  /**
   * 获取某个条件的值
   * @param props 条件表达式
   * @example
   * ```javascript
   * // 假设数值v123=3
   * const value = await this.bridge.invoke('utils', 'getConditionValue', 'v123>0'); // value == true
   * ```
   */
  public async getConditionValue(condition: string) {
    const value = await Utils.vObject.excuteCondition(condition);
    return value;
  }

  /**
   * 获取某个数值的名称
   * @param props 数值变量名
   * @example
   * ```javascript
   * // 假设数值v123对应的名称是'好感度'
   * const value = await this.bridge.getValue('v123'); // value == '好感度'
   * ```
   */
  public getValueName(props: string) {
    return Utils.vObject.getValueName(props);
  }

  /**
   * @internal
   */
  public on(event: string, callback: (...param: any[]) => void) {
    this.message.on(event, callback);
  }

  /**
   * @internal
   */
  public once(event: string, callback: (...param: any[]) => void) {
    this.message.once(event, callback);
  }

  /**
   * @internal
   */
  public off(event: string, callback: (...param: any[]) => void) {
    this.message.off(event, callback);
  }

  /**
   * @internal
   */
  public emit(event: string, ...data: any[]) {
    this.message.emit(event, ...data);
  }

  /**
   * 广播自定义事件给其他组件
   * @param event 事件名
   * @param data 事件触发时附带的数据
   * @example
   * ```javascript
   * // 组件A监听evt1
   * this.bridge.on('utils', 'evt1', (t)=>{
   *   // t == 1
   * });
   *
   * // 组件B触发事件evt1, 附带参数的值为1
   * this.bridge.invoke('utils', 'broadcast', 'evt1', 1);
   * ```
   */
  public broadcast(event: string, ...data: any[]) {
    Utils.broadcast(event, ...data);
  }

  /**
   * 像素单位转百分比
   * @param param.x x方向的像素值
   * @param param.y y方向的像素值
   * @example
   * ```javascript
   * // 假设视频画面是160px * 90px，那么16px * 9px 转换成百分比就是10% * 10%
   * const { x, y } = await this.bridge.invoke('utils', 'px2Percent', {
   *   x: 16,
   *   y: 9
   * });
   * // x == 0.1, y == 0.1
   * ```
   */
  public px2Percent({ x = 0, y = 0 }: { x?: number; y?: number }) {
    return {
      x: (x / Utils.contWidth) * 100,
      y: (y / Utils.contHeight) * 100,
    };
  }

  /**
   * 百分比转像素
   * @param param.x x方向的百分比
   * @param param.y y方向的百分比
   * @example
   * ```javascript
   * // 假设视频画面是160px * 90px，那么10% * 10% 转换成像素就是16px * 9px
   * const { x, y } = await this.bridge.invoke('utils', 'percent2Px', {
   *   x: 0.1, // 0.1 == 10%
   *   y: 0.1
   * });
   * // x == 16, y == 9
   * ```
   */
  public percent2Px({ x = 0, y = 0 }: { x?: number; y?: number }) {
    return {
      x: (x * Utils.contWidth) / 100,
      y: (y * Utils.contHeight) / 100,
    };
  }

  /**
   * 预加载图片
   * @param param 字符串或者字符串数组，字符串为图片的下载地址
   * @example
   * ```javascript
   * this.bridge.invoke('utils', 'preloadImg', 'https://xxx.com/xxx.jpg');
   * this.bridge.invoke('utils', 'preloadImg', ['https://xxx.com/xxx.jpg', ...]);
   * ```
   */
  public preloadImg(param: any) {
    if (param instanceof Array) {
      const urls = param as string[];
      urls.forEach((src) => {
        this.preloadImg(src);
      });
    } else if (typeof param === 'string') {
      const image = new Image();
      image.src = param;
    }
  }

  /**
   * kv存储的读接口
   * @param keys kv存储的key的数组
   * @example
   * ```javascript
   * // 假设test1存了字符串'a'，test2存了字符串'bb'
   * const { test1, test2 } = await this.bridge.invoke('utils', 'getString', ['test1', 'test2']);
   * // test1 == 'a' test2 == 'bb'
   * ```
   */
  public getString(keys: Array<string>) {
    return this.compsBridge.invoke(ChannelNameSpace.engine, 'getString', keys);
  }

  /**
   * kv存储的写接口
   * @param kv.key kv存储的key
   * @param kv.value kv存储的value
   * @example
   * ```javascript
   * await this.bridge.invoke('utils', 'setString', {
   *   key: 'test1',
   *   value: 'a'
   * });
   * ```
   */
  public setString(kv: { key: string; value: string }) {
    return this.compsBridge.invoke(ChannelNameSpace.engine, 'setString', kv);
  }

  /**
   * 用户监听表达式变化
  */
  public listenCondition(condition: string) {
    Utils.vObject.listenCondition(condition);
  }

  /**
   * @internal
   */
  public destroy() {
    this.hideInteract();
    this.destroyed = true;
    Utils.messageMap[this.uid] = null;
  }
}
